/**
 * Blog Post: How to Use Feasibility Study
 * Author: Business Advisor (Michael Torres)
 */

import type { BlogPost } from "../types";

export const howToUseFeasibilityPost: BlogPost = {
  slug: "how-to-use-feasibility-study",
  title:
    "Material Sourcing Feasibility: Compare Suppliers, Tariffs, and Lead Times",
  description:
    "Learn how to use Builder Buddy's Feasibility Study tool to compare construction material sourcing from Canada, USA, and China, including tariff calculations and government incentives.",
  excerpt:
    "Make informed sourcing decisions with our feasibility study tool. Compare suppliers, understand tariffs, and optimize your material procurement.",
  author: "business-advisor",
  category: "tools",
  featuredImage: "/blog/feasibility-hero.jpg",
  tags: [
    "feasibility",
    "material sourcing",
    "tariffs",
    "supply chain",
    "incentives",
  ],
  publishedAt: "2026-01-26",
  readingTime: 6,
  tableOfContents: [
    { id: "why-sourcing-matters", title: "Why Sourcing Matters", level: 2 },
    { id: "using-the-tool", title: "Using the Feasibility Tool", level: 2 },
    {
      id: "understanding-tariffs",
      title: "Understanding Canadian Tariffs",
      level: 2,
    },
    {
      id: "finding-incentives",
      title: "Finding Government Incentives",
      level: 2,
    },
  ],
  callToAction: {
    title: "Analyze your options",
    description: "Compare sourcing options for your construction materials.",
    buttonText: "Open Feasibility Study",
    buttonLink: "/feasibility",
  },
  content: `
In today's complex supply chain environment, smart material sourcing can make or break project budgets. Our Feasibility Study tool helps you compare options from Canada, USA, and China, factoring in tariffs, shipping costs, lead times, and available incentives.

## Why Sourcing Matters

Material costs typically represent 40-60% of construction project budgets. With recent tariff changes, supply chain disruptions, and fluctuating exchange rates, sourcing decisions have significant impact:

**Price Variations**
The same materials can vary 20-40% in price depending on source and timing. Understanding true landed costs (including shipping and duties) is essential.

**Lead Times**
Domestic materials typically arrive faster, but some specialty items only come from overseas. Planning for realistic lead times prevents project delays.

**Quality Considerations**
Different sources may have different quality standards. Understanding certifications and testing requirements helps ensure compliance.

**Tariff Impacts**
Recent Canadian tariffs on US and Chinese goods significantly affect construction materials. Our tool calculates actual duty costs.

## Using the Feasibility Tool

**Step 1: Select Material Categories**

Choose the materials you're sourcing:
- Lumber and wood products
- Steel and metals
- Concrete and masonry
- Finishes (cabinets, flooring, fixtures)
- Mechanical equipment
- Electrical equipment

Each category has different tariff rates and typical sources.

**Step 2: Enter Quantities and Specifications**

For accurate comparison, enter:
- Quantities needed
- Quality specifications
- Required certifications (if any)
- Delivery timeline

**Step 3: Compare Sources**

The tool analyzes three sourcing options:

**Canadian Suppliers**
- No tariffs
- Shorter lead times
- Support local economy
- Generally higher base prices

**US Suppliers**
- Subject to Canadian tariffs (varies by product)
- Moderate lead times
- Wide product selection
- Competitive pricing on many items

**Chinese Suppliers**
- Higher tariffs typically apply
- Longer lead times (4-8 weeks shipping)
- Lowest base prices
- May require quality verification

**Step 4: Review Analysis**

For each option, we show:
- Base material cost
- Shipping cost
- Duty/tariff amount
- Total landed cost
- Estimated delivery time
- Break-even analysis

## Understanding Canadian Tariffs

**Current Tariff Rates (2026)**

| Material Category | US Origin | China Origin |
|------------------|-----------|--------------|
| Lumber | 0-10% | 10-25% |
| Steel | 25% | 25%+ |
| Aluminum | 10% | 10-25% |
| Cabinets | 0-5% | 15-25% |
| General goods | varies | varies |

### Key Considerations

1. **Product Classification** - Tariff rates depend on specific HS codes (Harmonized System). Similar products can have very different rates.
2. **Country of Origin** - The country where goods are manufactured determines duty rates, not where they're shipped from.
3. **Free Trade Agreements** - CUSMA (Canada-US-Mexico Agreement) affects some rates. Products qualifying under CUSMA may have reduced duties.
4. **Anti-Dumping Duties** - Some products have additional anti-dumping or countervailing duties beyond standard tariffs.

Our tool incorporates current rates, but always verify with a customs broker for final import costs.

## Finding Government Incentives

Construction projects may qualify for various incentives that offset costs:

**Energy Efficiency Programs**
- Canada Greener Homes Grant
- Alberta residential rebates
- Commercial building efficiency incentives

**Green Building Incentives**
- LEED certification support
- Net-zero building programs
- Renewable energy incentives

**Regional Development**
- Calgary-specific development incentives
- Alberta investment tax credits
- Small business support programs

**How to Access Incentives:**

1. **Check eligibility** - Each program has specific requirements
2. **Apply early** - Many programs have limited funding
3. **Document compliance** - Keep records of qualifying materials and methods
4. **Work with professionals** - Some incentives require certified installers

Our tool identifies potentially applicable incentives based on your project type and location. Click through for application details and requirements.

### Operations Research Features

For larger projects, we include advanced analysis:

- **PERT Analysis** - Program Evaluation and Review Technique for estimating realistic delivery timelines with uncertainty
- **Monte Carlo Simulation** - Probabilistic analysis of total project costs given supply chain variables
- **Linear Programming** - Optimization of material allocation across multiple suppliers to minimize total cost while meeting timeline constraints

These features help sophisticated buyers make data-driven decisions on complex procurement.

### Best Practices

1. **Don't optimize solely for price** - Quality, reliability, and lead times matter too
2. **Build in buffer** - Supply chains can be unpredictable; order earlier than minimums require
3. **Consider total cost** - Include warehousing, handling, and quality inspection in comparisons
4. **Diversify when possible** - Single-source dependency creates risk
5. **Review regularly** - Tariffs, exchange rates, and shipping costs change; revisit decisions on long projects
`,
};
